import { Navigate, Outlet } from 'react-router-dom';
import loadable from '@loadable/component';
import { LayoutProvider } from './layout/LayoutContext';
import { ProjectContextProvider } from './context/project.provider';
import { AuthProvider, useAuth } from './context/auth/auth.provider';

const AppLayout = loadable(() => import('./layout/AppLayout'), {
  resolveComponent: (components) => components.default,
});

const NotFoundPage = loadable(() => import('./not-found/page'));
const LoginPage = loadable(() => import('../app/auth/login/page'));
const SetupPage = loadable(() => import('../app/auth/setup/page'));
const InvitePage = loadable(() => import('../app/auth/invite/page'));
const SignupPage = loadable(() => import('../app/auth/signup/page'));
const VerifyEmailPage = loadable(() => import('../app/auth/verify-email/page'));
const ForgotPasswordPage = loadable(() => import('../app/auth/forgot-password/page'));
const ResetPasswordPage = loadable(() => import('../app/auth/reset-password/page'));
const AuthCallbackPage = loadable(() => import('../app/auth/auth-callback/page'));
const OnboardingPage = loadable(() => import('../app/onboarding/page'));

export const RootLayout = () => {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
};

const LayoutWrapper = () => {
  return (
    <LayoutProvider>
      <ProjectContextProvider>
        <AppLayout />
      </ProjectContextProvider>
    </LayoutProvider>
  );
};

const PromptLabPage = loadable(() => import('../app/prompt-lab/page'));
const VersionsPage = loadable(() => import('../app/prompt-lab/versions/page'));
const PromptStudioPage = loadable(() => import('../app/prompt-studio/page'));
const ProvidersPage = loadable(() => import('../app/providers/page'));
const InteractionsPage = loadable(() => import('../app/interactions/page'));
const InteractionDetailPage = loadable(() => import('../app/interactions/interaction-detail-page'));
const AlertLogsPage = loadable(() => import('../app/alert-logs/page'));
const CostAnalyticsPage = loadable(() => import('../app/cost-analytics/page'));
const DashboardPage = loadable(() => import('../app/dashboard/page'));
const HowToPage = loadable(() => import('../app/how-to/page'));
const ModelCatalogPage = loadable(() => import('../app/model-catalog/page'));
const ModelCatalogDetailPage = loadable(() => import('../app/model-catalog/detail-page'));
const UsersPage = loadable(() => import('../app/users/page'));

const CenteredLoading: React.FC = () => (
  <div className="min-h-screen flex align-items-center justify-content-center">
    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
  </div>
);

const SetupGuard: React.FC = () => {
  const { isLoading, needsSetup, isAuthenticated } = useAuth();

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (!needsSetup) {
    return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
  }

  return <Outlet />;
};

const LoginGuard: React.FC = () => {
  const { isLoading, needsSetup, isAuthenticated, user } = useAuth();

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  if (isAuthenticated) {
    if (user && !user.onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

const AuthenticatedGuard: React.FC = () => {
  const { isLoading, needsSetup, isAuthenticated, user } = useAuth();

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user && !user.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
};

const OnboardingGuard: React.FC = () => {
  const { isLoading, needsSetup, isAuthenticated, user } = useAuth();

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user && user.onboardingCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

export const baseRouter = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        path: 'invite/:token',
        element: <InvitePage />,
      },
      {
        element: <SetupGuard />,
        children: [
          {
            path: 'setup',
            element: <SetupPage />,
          },
        ],
      },
      {
        element: <LoginGuard />,
        children: [
          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'signup',
            element: <SignupPage />,
          },
          {
            path: 'forgot-password',
            element: <ForgotPasswordPage />,
          },
          {
            path: 'reset-password',
            element: <ResetPasswordPage />,
          },
        ],
      },
      {
        path: 'verify-email',
        element: <VerifyEmailPage />,
      },
      {
        path: 'auth/callback',
        element: <AuthCallbackPage />,
      },
      {
        element: <OnboardingGuard />,
        children: [
          {
            path: 'onboarding',
            element: <OnboardingPage />,
          },
        ],
      },
      {
        element: <AuthenticatedGuard />,
        children: [
          {
            path: '/',
            element: <LayoutWrapper />,
            children: [
              {
                index: true,
                element: <DashboardPage />,
              },
              {
                path: 'dashboard',
                element: <DashboardPage />,
              },
              {
                path: 'how-to',
                element: <HowToPage />,
              },
              {
                path: 'model-catalog',
                element: <ModelCatalogPage />,
              },
              {
                path: 'model-catalog/:recordId',
                element: <ModelCatalogDetailPage />,
              },
              {
                path: 'prompt-lab',
                element: <PromptLabPage />,
              },
              {
                path: 'prompt-lab/:promptId/versions',
                element: <VersionsPage />,
              },
              {
                path: 'prompt-studio',
                element: <PromptStudioPage />,
              },
              {
                path: 'providers',
                element: <ProvidersPage />,
              },
              {
                path: 'interactions',
                element: <InteractionsPage />,
              },
              {
                path: 'alert-logs',
                element: <AlertLogsPage />,
              },
              {
                path: 'interactions/:interactionId',
                element: <InteractionDetailPage />,
              },
              {
                path: 'cost-analytics',
                element: <CostAnalyticsPage />,
              },
              {
                path: 'users',
                element: <UsersPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];
